import { useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { DayPicker } from 'react-day-picker'
import { CalendarDays } from 'lucide-react'
import 'react-day-picker/style.css'
import { formatDay } from '../../lib/format'
import { BOUTIQUE_LOCALE } from '../../lib/boutique'

/**
 * A `YYYY-MM-DD` chosen from a calendar rather than typed into the browser's
 * own date control.
 *
 * The native control was one input for three jobs and did none of them here:
 * it looks different in every browser, it cannot show a month at a glance
 * beside a grid that is also showing one, and on a shop-floor tablet its
 * picker opens as a system sheet that hides the list underneath.
 *
 * Days are handled in UTC throughout — the console's day is the same day the
 * API means (see `lib/months.ts`), so the value is split and rebuilt rather
 * than passed through a local-timezone `Date`.
 */
export function DatePicker({
  value,
  onChange,
  label,
  placeholder = 'Pick a date',
  onClear,
  max,
}: {
  /** `''` when nothing is picked yet — an optional date, such as a wedding nobody has set. */
  readonly value: string
  readonly onChange: (day: string) => void
  readonly label: string
  readonly placeholder?: string
  /** Offer a way back to no date at all. Absent for a date that is always required. */
  readonly onClear?: () => void
  /** The latest day that can be picked, `YYYY-MM-DD` — a payment's paid date is never after today. */
  readonly max?: string
}) {
  const [open, setOpen] = useState(false)
  const locale = BOUTIQUE_LOCALE
  const [y, m, d] = value.split('-').map(Number)
  // `DayPicker` works in local time, so the selected day is handed to it as a
  // local date with the same numbers on it — never as a parsed UTC instant,
  // which west of Greenwich would select the day before.
  const selected = y && m && d ? new Date(y, m - 1, d) : undefined
  const [maxY, maxM, maxD] = (max ?? '').split('-').map(Number)
  const latest = maxY && maxM && maxD ? new Date(maxY, maxM - 1, maxD) : undefined

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="st-btn-ghost" aria-label={value ? `${label}: ${formatDay(value, locale)}` : label}>
          <CalendarDays size={14} aria-hidden />
          <span className="tabular-nums">{value ? formatDay(value, locale) : placeholder}</span>
        </button>
      </Popover.Trigger>
      {onClear && value ? (
        <button type="button" className="st-btn-quiet ml-1" onClick={onClear} aria-label={`Clear ${label.toLowerCase()}`}>
          Clear
        </button>
      ) : null}
      <Popover.Portal>
        <Popover.Content className="st-popover" align="start" sideOffset={6}>
          <DayPicker
            className="st-daypicker"
            mode="single"
            selected={selected}
            defaultMonth={selected}
            {...(latest ? { disabled: { after: latest }, endMonth: latest } : {})}
            onSelect={(day) => {
              if (!day) return
              const iso = `${day.getFullYear()}-${`${day.getMonth() + 1}`.padStart(2, '0')}-${`${day.getDate()}`.padStart(2, '0')}`
              onChange(iso)
              setOpen(false)
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
